/**
 * What can be tested, and at what level.
 *
 * Until now the app knew about one equipment type and one Cx level, both
 * implicit. That was fine while it was proving itself on LV switchboards; it
 * stops being fine the moment a project holds a transformer, a genset and a UPS,
 * because the engineer needs to say *what* they are standing in front of before
 * the app can show them a script.
 *
 * So this is the registry the selection screens read: every equipment type the
 * platform can test, what it is made of, and which Cx levels it has content for.
 *
 * Three rules are enforced here rather than left to the screens:
 *
 *  - **A level with no content cannot be chosen.** CxL1, L2, L4 and L5 are named
 *    so the shape is right and the picker does not have to be rebuilt later, but
 *    they are `available: false` and `catalogueFor` refuses them. An empty script
 *    in front of an engineer on site is worse than a closed door.
 *  - **A test only reaches equipment it names.** `catalogueFor` filters on the
 *    equipment type, so an MV test cannot appear on an LV board even if someone
 *    ticks it — the silo rule from `equipment-silo.md`, enforced in the domain.
 *  - **Every type carries a short code.** Report file names and evidence folder
 *    paths are built from it, so it is part of the contract, not a label.
 */

import type { CatalogueTest, CxLevel } from './catalogue.js';
import { CXL3_LV_SWITCHBOARD } from './cxl3-catalogue.js';
import { CXL3_ATS } from './catalogues/ats.js';
import { CXL3_BUSDUCT } from './catalogues/busduct.js';
import { CXL3_GENERATOR } from './catalogues/generator.js';
import { CXL3_MCC } from './catalogues/mcc.js';
import { CXL3_MV_SWITCHBOARD } from './catalogues/mvsb.js';
import { CXL3_TRANSFORMER } from './catalogues/transformer.js';
import { CXL3_UPS } from './catalogues/ups.js';

/**
 * The `EquipmentType` values. These are the Choice column on `Cx Assets`,
 * `Cx Templates` and `Cx Instances`, so the strings are the contract — renaming
 * one orphans every asset already carrying it.
 */
export type EquipmentType =
  | 'LV Switchboard'
  | 'MV Switchboard'
  | 'MCC'
  | 'Transformer'
  | 'Generator'
  | 'UPS'
  | 'ATS'
  | 'Busduct';

/** What a piece of equipment is made of, which decides how the app navigates it. */
export type ChildKind =
  /** Ways/cubicles tested one at a time — the breaker page. */
  | 'breaker'
  /** Outgoing circuits or tap-offs, tested as a list under the board script. */
  | 'circuit'
  /** Tested as one unit: there is no level below the asset. */
  | 'unit';

export interface EquipmentDef {
  type: EquipmentType;
  /** Short code used in report file names and evidence folder paths. */
  shortCode: string;
  name: string;
  discipline: 'Electrical' | 'Mechanical';
  children: ChildKind;
  /** What the thing below this asset is called on screen. Null when `children` is 'unit'. */
  childNoun: string | null;
  /** One line for the person choosing, on the picker. */
  note: string;
}

export const EQUIPMENT: readonly EquipmentDef[] = [
  {
    type: 'LV Switchboard',
    shortCode: 'LVSB',
    name: 'LV switchboard',
    discipline: 'Electrical',
    children: 'breaker',
    childNoun: 'way',
    note: 'Main and sub switchboards. Incomer and outgoing ways tested one at a time.',
  },
  {
    type: 'MV Switchboard',
    shortCode: 'MVSB',
    name: 'MV switchboard',
    discipline: 'Electrical',
    children: 'breaker',
    childNoun: 'panel',
    note: 'Ring main units and MV panels. Built from the CxA script, revision 02.',
  },
  {
    type: 'MCC',
    shortCode: 'MCC',
    name: 'Motor control centre',
    discipline: 'Electrical',
    children: 'breaker',
    childNoun: 'starter',
    note: 'Shares the switchboard tests and adds the starter, overload and rotation checks.',
  },
  {
    type: 'Transformer',
    shortCode: 'TX',
    name: 'Transformer',
    discipline: 'Electrical',
    children: 'unit',
    childNoun: null,
    note: 'Energised checks only. Ratio, IR and winding resistance are CxL2 and are not here.',
  },
  {
    type: 'Generator',
    shortCode: 'GEN',
    name: 'Generator set',
    discipline: 'Electrical',
    children: 'unit',
    childNoun: null,
    note: 'Start, run and protection shutdowns. Load bank and paralleling are CxL4.',
  },
  {
    type: 'UPS',
    shortCode: 'UPS',
    name: 'UPS',
    discipline: 'Electrical',
    children: 'unit',
    childNoun: null,
    note: 'Mode transfers and battery checks. A full autonomy discharge is CxL4.',
  },
  {
    type: 'ATS',
    shortCode: 'ATS',
    name: 'Automatic transfer switch',
    discipline: 'Electrical',
    children: 'unit',
    childNoun: null,
    note: 'Transfer and retransfer with their timers, and the interlock that stops a parallel.',
  },
  {
    type: 'Busduct',
    shortCode: 'BD',
    name: 'Busduct / busway',
    discipline: 'Electrical',
    children: 'circuit',
    childNoun: 'tap-off',
    note: 'Run checks plus each tap-off unit. Thermography at joints is on by default here.',
  },
];

export interface LevelDef {
  level: CxLevel;
  name: string;
  /** False until the level has content. The picker shows it, disabled. */
  available: boolean;
  note: string;
}

/**
 * All five levels, only one of them open.
 *
 * They are listed rather than omitted so the picker's shape is settled now and a
 * later level is content, not a rewrite. `available` is the gate, and it is
 * checked in `catalogueFor` as well as in the UI — a screen is not a guard.
 */
export const CX_LEVELS: readonly LevelDef[] = [
  { level: 'CxL1', name: 'Factory and delivery', available: false, note: 'FAT, delivery and storage checks. Not built.' },
  { level: 'CxL2', name: 'Static / pre-energisation', available: false, note: 'Dead-bus: IR, continuity, ratio, contact resistance. Not built.' },
  { level: 'CxL3', name: 'Startup testing', available: true, note: 'Energised startup and pre-functional testing.' },
  { level: 'CxL4', name: 'Functional performance', available: false, note: 'Load bank, autonomy, sequence of operation. Not built.' },
  { level: 'CxL5', name: 'Integrated systems test', available: false, note: 'Whole-facility failure scenarios. Not built.' },
];

export class EquipmentError extends Error {}

const BY_TYPE = new Map(EQUIPMENT.map((e) => [e.type, e]));
const BY_LEVEL = new Map(CX_LEVELS.map((l) => [l.level, l]));

/** Every CxL3 test the platform holds, in the order equipment is listed above. */
const CXL3: readonly CatalogueTest[] = [
  ...CXL3_LV_SWITCHBOARD,
  ...CXL3_MV_SWITCHBOARD,
  ...CXL3_MCC,
  ...CXL3_TRANSFORMER,
  ...CXL3_GENERATOR,
  ...CXL3_UPS,
  ...CXL3_ATS,
  ...CXL3_BUSDUCT,
];

/** Level → the tests written for it. Adding CxL2 later means adding one entry. */
const CATALOGUES: Partial<Record<CxLevel, readonly CatalogueTest[]>> = { CxL3: CXL3 };

export const equipmentDef = (type: string): EquipmentDef | undefined => BY_TYPE.get(type as EquipmentType);
export const levelDef = (level: string): LevelDef | undefined => BY_LEVEL.get(level as CxLevel);

export const isAvailable = (level: string): boolean => levelDef(level)?.available === true;

/**
 * The tests for one equipment type at one level.
 *
 * Refuses rather than returning nothing, because the two failures look identical
 * on screen and mean opposite things: an unknown type is a bug in the caller, a
 * closed level is a decision, and an equipment type with no content at an open
 * level is a gap somebody needs to be told about.
 */
export function catalogueFor(type: string, level: CxLevel): CatalogueTest[] {
  const def = BY_TYPE.get(type as EquipmentType);
  if (!def) throw new EquipmentError(`${type} is not an equipment type this platform tests`);

  const lvl = BY_LEVEL.get(level);
  if (!lvl) throw new EquipmentError(`${level} is not a Cx level`);
  if (!lvl.available) throw new EquipmentError(`${level} — ${lvl.name} — has no tests yet`);

  const all = CATALOGUES[level] ?? [];
  const tests = all.filter((t) => t.equipmentTypes.includes(def.type));
  if (tests.length === 0) {
    throw new EquipmentError(`no ${level} tests are written for ${def.name}`);
  }
  return tests;
}

/** Which levels a given equipment type has content for. Drives the level picker. */
export function levelsFor(type: string): LevelDef[] {
  return CX_LEVELS.map((l) => {
    if (!l.available) return l;
    try {
      catalogueFor(type, l.level);
      return l;
    } catch {
      // Open level, no content for this equipment: shown, but not choosable.
      return { ...l, available: false, note: `No ${l.level} tests written for this equipment yet.` };
    }
  });
}

/** What the picker offers, with the level count each type can actually run. */
export interface EquipmentOption extends EquipmentDef {
  availableLevels: CxLevel[];
}

export function equipmentOptions(): EquipmentOption[] {
  return EQUIPMENT.map((e) => ({
    ...e,
    availableLevels: levelsFor(e.type).filter((l) => l.available).map((l) => l.level),
  }));
}

/**
 * The choice an engineer makes before anything else loads.
 *
 * Held as one value rather than three loose fields so it cannot be half-made:
 * there is no state in which a level is chosen and the equipment is not.
 */
export interface TestTarget {
  projectId: number;
  equipmentType: EquipmentType;
  level: CxLevel;
}

export function makeTarget(projectId: number, equipmentType: string, level: string): TestTarget {
  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new EquipmentError('a target needs a project');
  }
  const def = BY_TYPE.get(equipmentType as EquipmentType);
  if (!def) throw new EquipmentError(`${equipmentType} is not an equipment type this platform tests`);
  // Throws for a closed level or an equipment type with nothing written for it.
  catalogueFor(def.type, level as CxLevel);
  return { projectId, equipmentType: def.type, level: level as CxLevel };
}

/**
 * The stem for this target's report files and evidence folder.
 *
 * `report-output.md` fixes the file names and `equipment-silo.md` puts the
 * equipment type into the evidence path. Both are built from here so they cannot
 * drift apart.
 */
export function targetSlug(target: TestTarget): string {
  const def = BY_TYPE.get(target.equipmentType);
  if (!def) throw new EquipmentError(`${target.equipmentType} is not an equipment type this platform tests`);
  return `${def.shortCode}_${target.level}`;
}
